import React, { useEffect } from 'react';
import { returnedTypes, measurementType } from './MeasurementEnums';
import { observer } from 'mobx-react-lite';
import useStores from '../../hooks/useStores';
import MeasurementField from './MeasurementField';
import {
  CheckIfFirstRender,
  convert,
  isPoundInchType,
  isToCovertValues,
} from './MeasurementHelper';
import { StandardTextFieldProps } from '@mui/material';
import {
  Controller,
  SetValueConfig,
  Control,
  UseFormGetValues,
  FieldValues,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DEFAULT_NS } from '../../i18n/types';

interface MeasurementWeightContainerProps extends StandardTextFieldProps {
  defaultValue?: number;
  returnedType: returnedTypes;
  isHideMeasurementsIcon?: boolean;
  inputClassName?: string;
  textFieldClasses?: {
    root?: string;
    disabled?: string;
    wrapperClassName?: string;
    endAdornmentClassName?: string;
    input?: string;
  };
  setValue(name: string, value: any, config?: SetValueConfig): void; // eslint-disable-line
  getValues: UseFormGetValues<FieldValues>;
  control: Control;
  measureType: measurementType;
  readOnly?: boolean;
  name?: string;
  validationErrorMsg?: string;
}

function MeasurementWeightContainer({
  defaultValue,
  setValue,
  getValues,
  control,
  measureType,
  readOnly = false,
  required,
  isHideMeasurementsIcon,
  textFieldClasses,
  name,
  validationErrorMsg,
  ...textFieldProps
}: MeasurementWeightContainerProps) {
  const fieldName = name || 'weight';
  const { t } = useTranslation(DEFAULT_NS, {
    keyPrefix: 'profile.content.fields',
  });
  const { measurementsStore } = useStores();
  const isMount = CheckIfFirstRender();
  useEffect(() => {
    if (
      isToCovertValues(isMount, measurementsStore.unitsMapping, measureType)
    ) {
      const convertNumber = convert(
        Number(getValues(fieldName)),
        measureType,
        measurementsStore.unitsMapping
      );
      setValue(fieldName, convertNumber, {});
    }
  }, [measurementsStore.unitsMapping]);

  useEffect(() => {
    return () => {
      setValue(fieldName, defaultValue, {});
    };
  }, []);

  return (
    <Controller
      name={fieldName}
      control={control}
      defaultValue={defaultValue}
      rules={{
        min: {
          value: isPoundInchType(measurementsStore.unitsMapping) ? 24 : 11,
          message:
            validationErrorMsg ||
            'Please ensure that weight value is greater than 10',
        },
        max: {
          value: isPoundInchType(measurementsStore.unitsMapping) ? 1101 : 499,
          message:
            validationErrorMsg ||
            'Please ensure that weight value is less than 500',
        },
      }}
      render={({ field: { name, ...props } }) => (
        <MeasurementField
          label={t('weight')}
          measureType={measurementType.weight}
          returnedType={returnedTypes.unit}
          getValues={getValues}
          disabled={readOnly}
          name={name}
          required={required}
          textFieldClasses={textFieldClasses}
          isHideMeasurementsIcon={isHideMeasurementsIcon}
          placeholder={textFieldProps?.placeholder}
          {...props}
          onBlur={(event) => {
            props.onBlur();
            textFieldProps?.onBlur?.(event);
          }}
          onInput={(event) => {
            textFieldProps?.onInput?.(event);
          }}
          helperText={(textFieldProps?.helperText as string) ?? ''}
          error={textFieldProps?.error ?? false}
        />
      )}
    />
  );
}
export default observer(MeasurementWeightContainer);
